import {
  HHButton,
  HHStack,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useState } from 'react';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useRevenueSharePageData } from '../../hooks/revenue-share-hooks';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  ScopeableType,
  useCreateRevenueShareMutation,
  useUpdateRevenueShareMutation,
} from '../../types';
import { ActiveStatus } from '../../utils/contract-utils';
import { IRevenueShare, toEditableScopes } from '../../utils/discount-utils';
import { ScopeItem } from '../form-fields/scope-selector/scope-selector-constants';
import { RevenueShareForm } from './revenue-share-form/form';
import {
  ParsedRevenueShareValues,
  RevenueShareFormValues,
} from './revenue-share-form/form-constants';
import RevenueSharesList from './revenue-shares-list';

enum RevenueShareStatusFilter {
  All = 'all',
  Active = 'active',
}

/**
 * Maps a persisted revenue share into the edit form's initial values. Global
 * definitions load as `scopeableType: Global` with no selections regardless of
 * whether they carry an explicit Global scope row, which `toEditableScopes`
 * filters out.
 */
export const buildRevenueShareInitialValues = (
  revShare: IRevenueShare,
): RevenueShareFormValues => {
  const existingScopes: ScopeItem[] = toEditableScopes(revShare.scopes);
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  return {
    clientId: revShare.clientId ?? null,
    partnershipId: revShare.partnershipId ?? null,
    effectiveYear: revShare.effectiveYear,
    discount: revShare.details[0]?.discount ?? null,
    applyToPrice: revShare.applyToPrice,
    scopeableType:
      existingScopes.length > 0
        ? existingScopes[0].scopeableType
        : ScopeableType.Global,
    scopes: existingScopes,
  };
};

const RevenueSharesGlobalView = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [statusFilter, setStatusFilter] = useState(
    RevenueShareStatusFilter.All,
  );

  const {
    revenueShares: allRevenueShares,
    revenueSharesLoading,
    clients,
    clientsLoading,
    partnerships,
    partnershipsLoading,
    packages,
    packagesLoading,
    clientMap,
    partnershipMap,
  } = useRevenueSharePageData();

  const [createRevenueShare] = useCreateRevenueShareMutation();
  const [updateRevenueShare] = useUpdateRevenueShareMutation();

  const filteredRevenueShares =
    statusFilter === RevenueShareStatusFilter.Active
      ? allRevenueShares.filter(r => r.status === ActiveStatus.Active)
      : allRevenueShares;

  const openDrawerForCreate = useCallback((): void => {
    const handleCreate = async (
      values: ParsedRevenueShareValues,
    ): Promise<void> => {
      try {
        await createRevenueShare({
          variables: {
            revenueShareInput: {
              // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId/clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
              partnershipId: values.partnershipId,
              clientId: values.clientId,
              effectiveYear: values.effectiveYear,
              discount: values.discount,
              applyToPrice: values.applyToPrice,
              scopes: values.scopes.map(s => ({
                scopeableType: s.scopeableType,
                scopeableId: s.scopeableId ?? 0,
              })),
            },
          },
          refetchQueries: ['getRevenueShares'],
        });
        setSnackbarState('Revenue share created successfully', 'success', 3000);
        onClose();
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Error creating revenue share';
        setSnackbarState(message, 'error', 5000);
      }
    };

    setDrawerContent(
      <HHStack padding={4}>
        <RevenueShareForm
          onSubmit={handleCreate}
          allRevenueShares={allRevenueShares}
          clients={clients}
          clientsLoading={clientsLoading}
          partnerships={partnerships}
          partnershipsLoading={partnershipsLoading}
          packages={packages}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add Revenue Share</HHTypography>,
      '50%',
      'temporary',
    );
  }, [
    setDrawerContent,
    createRevenueShare,
    setSnackbarState,
    onClose,
    allRevenueShares,
    clients,
    clientsLoading,
    partnerships,
    partnershipsLoading,
    packages,
  ]);

  const handleEditClick = useCallback(
    (revenueShareDefinitionId: number): void => {
      const revShare = allRevenueShares.find(
        r => r.revenueShareDefinitionId === revenueShareDefinitionId,
      );
      if (!revShare) return;

      const initialValues = buildRevenueShareInitialValues(revShare);

      const handleUpdate = async (
        values: ParsedRevenueShareValues,
      ): Promise<void> => {
        try {
          await updateRevenueShare({
            variables: {
              revenueShareInput: {
                revenueShareDefinitionId,
                effectiveYear: values.effectiveYear,
                applyToPrice: values.applyToPrice,
                discount: values.discount,
                scopes: values.scopes.map(s => ({
                  scopeableType: s.scopeableType,
                  scopeableId: s.scopeableId ?? 0,
                })),
              },
            },
            refetchQueries: ['getRevenueShares'],
          });
          setSnackbarState(
            'Revenue share updated successfully',
            'success',
            3000,
          );
          onClose();
        } catch (e) {
          const message =
            e instanceof Error ? e.message : 'Error updating revenue share';
          setSnackbarState(message, 'error', 5000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <RevenueShareForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            id={revenueShareDefinitionId}
            allRevenueShares={allRevenueShares}
            clients={clients}
            clientsLoading={clientsLoading}
            partnerships={partnerships}
            partnershipsLoading={partnershipsLoading}
            packages={packages}
            wholeFormDisabled={revShare.status !== ActiveStatus.Future}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit Revenue Share</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [
      allRevenueShares,
      setDrawerContent,
      updateRevenueShare,
      setSnackbarState,
      onClose,
      clients,
      clientsLoading,
      partnerships,
      partnershipsLoading,
      packages,
    ],
  );

  return (
    <HHStack flex={1} direction="column" minHeight="0">
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHStack direction="row" alignItems="center" spacing={2}>
          <HHTypography hhVariant="section-title">Revenue Shares</HHTypography>
          <HHToggleButtonGroup
            exclusive
            value={statusFilter}
            onChange={(_, newValue): void => {
              if (newValue !== null) {
                setStatusFilter(newValue as RevenueShareStatusFilter);
              }
            }}
            size="small"
            aria-label="filter revenue shares by status"
            sx={{ height: '40px' }}
          >
            <HHToggleButton
              hhVariant="variant-bypass"
              value={RevenueShareStatusFilter.All}
              sx={{ borderRadius: '4px 0 0 4px', textTransform: 'none' }}
            >
              View All
            </HHToggleButton>
            <HHToggleButton
              hhVariant="variant-bypass"
              value={RevenueShareStatusFilter.Active}
              sx={{ borderRadius: '0 4px 4px 0', textTransform: 'none' }}
            >
              View Active
            </HHToggleButton>
          </HHToggleButtonGroup>
        </HHStack>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={openDrawerForCreate}
          >
            Add Revenue Share
          </HHButton>
        )}
      </HHStack>
      <HHStack
        flex={1}
        direction="column"
        spacing={4}
        sx={{ overflowY: 'auto', padding: 6, paddingTop: 0 }}
      >
        <RevenueSharesList
          revenueSharesData={filteredRevenueShares}
          loading={revenueSharesLoading || packagesLoading}
          availablePackages={packages}
          onEditRevenueShareClick={canWrite ? handleEditClick : undefined}
          clientMap={clientMap}
          partnershipMap={partnershipMap}
        />
      </HHStack>
    </HHStack>
  );
};

export default RevenueSharesGlobalView;
